"use client"

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faFire, faTrophy, faPlayCircle, faDice, faChevronRight, faSignal } from '@fortawesome/free-solid-svg-icons'
import Link from 'next/link'
import { generateMatchUrl } from '@/lib/slug-utils'
import { useLiveScore } from '@/hooks/useLiveScore'
import { Match } from '@/lib/database'

interface GlassMatchCardProps {
	match: Match
	isLoaded?: boolean
}

export default function GlassMatchCard({ match, isLoaded = false }: GlassMatchCardProps) {
	const { getCurrentScore, loading: scoreLoading } = useLiveScore({
		matchId: match.id,
		id_api: match.id_api,
		match_status: match.match_status,
		home_score: match.home_score,
		away_score: match.away_score,
		autoRefresh: true,
		refreshInterval: 30000
	})

	const currentScore = getCurrentScore()
	const isLive = match.match_status === '3'
	const isFinished = match.match_status === '2' || match.match_status === '4'
	const isUpcoming = match.match_status === '1'

	const matchUrl = generateMatchUrl(
		match.id,
		match.team_hone || 'Đội nhà',
		match.team_away || 'Đội khách',
		match.league_name,
		match.time_start,
		match.caster
	)

	const formatTime = (timeStart?: string) => {
		if (!timeStart) return ''
		const d = new Date(timeStart)
		return `${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} ${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}`
	}

	const defaultCasterAvatar = 'https://70303d714.vws.vegacdn.vn/cakhiastatic/avtblv/avatar-default.png'
	const firstCaster = (match.caster || '').split(',').map(s => s.trim()).filter(Boolean)[0]

	return (
		<div className="relative rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border" style={{ background: 'var(--matchcard-bg)', color: 'var(--matchcard-text)', borderColor: 'var(--matchcard-border)' }}>
			{/* Clickable overlay */}
			<Link href={matchUrl} className="absolute inset-0" aria-label="Xem chi tiết trận" />

			{/* Header */}
			<div className="relative z-10 px-3 pt-2 pb-3 border-b" style={{ borderColor: 'var(--matchcard-divider)' }}>
				<div className="flex justify-between items-center gap-2 text-sm" style={{ color: 'var(--matchcard-muted-text)' }}>
					<div className="font-medium flex items-center gap-1 uppercase flex-1" style={{ color: 'var(--primary)' }}>
						<FontAwesomeIcon icon={faFire} style={{ color: 'var(--matchcard-hot-icon)' }} />
						Hot
					</div>
					<div className="flex items-center justify-center gap-2">
						<FontAwesomeIcon icon={faTrophy} className="text-lg" style={{ color: '#9ca3af' }} />
						<span className="text-sm whitespace-nowrap text-ellipsis overflow-hidden max-w-[150px] md:max-w-[200px]" style={{ color: 'var(--matchcard-text)' }}>
							{match.league_name || 'Giải đấu'}
						</span>
					</div>
					<div className="h-6 flex flex-1 justify-end">
						{isLive && (
							<div className="flex items-center justify-center gap-2">
								<div className="relative flex items-center">
									<span className="absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75 animate-ping" />
									<span className="relative inline-flex rounded-full h-3 w-3 bg-red-600" />
								</div>
								<span className="text-xs font-semibold text-red-600">LIVE</span>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Body */}
			<div className="relative z-10 px-2">
				<div className="flex flex-row justify-between items-stretch gap-3">
					{/* Home */}
					<div className="flex-1 items-center justify-start flex flex-col gap-4 text-center">
						<div className="inline-flex justify-center items-center">
							{match.team_hone_avatar && (
								<img
									className="w-12 h-12 object-cover rounded-full"
									src={match.team_hone_avatar.startsWith('http') ? match.team_hone_avatar : `https://okapi999.com${match.team_hone_avatar}`}
									alt={match.team_hone || 'Đội nhà'}
								/>
							)}
						</div>
						<h5 className="team-name text-sm lg:text-base lg:leading-5 font-medium line-clamp-2 min-h-10 lg:min-h-0" style={{ color: 'var(--matchcard-text)' }}>
							{match.team_hone || 'Đội nhà'}
						</h5>
					</div>

					{/* Center score/time */}
					<div className="text-2xl font-medium flex flex-col items-center justify-center gap-3 min-w-[90px]">
						<span className="time text-sm lg:text-base capitalize" style={{ color: 'var(--matchcard-muted-text)' }}>
							{isLive ? 'Hiệp đấu' : isFinished ? '' : ''}
						</span>
						{scoreLoading ? (
							<span style={{ color: 'var(--matchcard-muted-text)' }}>-- - --</span>
						) : (
							<span>
								<span>{currentScore.home_score ?? (isFinished ? '0' : '0')}</span> -{' '}
								<span>{currentScore.away_score ?? (isFinished ? '0' : '0')}</span>
							</span>
						)}
						<span className="time text-sm font-medium" style={{ color: 'var(--matchcard-text)' }}>{formatTime(match.time_start)}</span>
					</div>

					{/* Away */}
					<div className="flex-1 items-center justify-start flex flex-col gap-4 text-center">
						<div className="inline-flex justify-center items-center">
							{match.team_away_avatar && (
								<img
									className="w-12 h-12 object-cover rounded-full"
									src={match.team_away_avatar.startsWith('http') ? match.team_away_avatar : `https://okapi999.com${match.team_away_avatar}`}
									alt={match.team_away || 'Đội khách'}
								/>
							)}
						</div>
						<h5 className="team-name text-sm lg:text-base lg:leading-5 font-medium line-clamp-2 min-h-10 lg:min-h-0" style={{ color: 'var(--matchcard-text)' }}>
							{match.team_away || 'Đội khách'}
						</h5>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className="relative z-10 flex justify-between items-center gap-2 px-2 pb-2 pt-3 mt-3 border-t" style={{ borderColor: 'var(--matchcard-divider)' }}>
				{/* BLV pill */}
				<div className="inline-flex gap-1.5 items-center font-semibold overflow-hidden text-sm rounded-3xl" style={{ backgroundColor: 'var(--blv-pill-bg)' }}>
					<img src={defaultCasterAvatar} alt={firstCaster || 'BLV'} className="w-8 h-8 rounded-full m-0.5" />
					<span className="pr-3 py-1 whitespace-nowrap max-w-[160px] text-white text-ellipsis overflow-hidden">{firstCaster || 'BLV Trực tiếp'}</span>
				</div>

				{/* Actions */}
				<div className="flex gap-1 shrink-0">
					<Link href={matchUrl} className="py-1 px-4 rounded-3xl font-semibold text-sm capitalize leading-5 text-white" style={{ backgroundColor: 'var(--btn-live-bg)' }}>
						Trực tiếp
					</Link>
					<a href="https://018789win.com/?f=2857311" target="_blank" rel="noopener noreferrer" className="text-white py-1 px-3 rounded-3xl font-semibold text-sm capitalize" style={{ backgroundColor: 'var(--btn-bet-bg)' }}>
						Đặt cược
					</a>
				</div>
			</div>
		</div>
	)
} 